import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, IconButton } from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../store/color/colorSlice';
import { findColor, createColor, patchColor } from '../../store/color/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import { omit } from 'lodash';
import {
  reset as resetColor,
  setStatus as setColorStatus,
} from '../../store/color/colorSlice';
// import {
//   reset as resetSelectCountry,
//   setStatus as setSelectCountryStatus,
// } from '../../store/selectCountry/selectCountrySlice';
import ColorFormModal from './ColorFormModal';
import colorSchema from '../../validation/colorSchema';
import { Color } from '../../store/color/api/interface';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.color);
  const [search, setSearch] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findColor({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetColor());
  }, [search]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      colorCode: '',
      labelEn: '',
      labelAr: '',
      // colorType: '',
      position: '',
      activeState: true,
    },
    validationSchema: colorSchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchColor(values)).unwrap();
        }
        if (!values.id) {
          const reqBody = omit(values, ['id']);
          await dispatch(createColor(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        // dispatch(resetSelectCountry());
        // dispatch(setSelectCountryStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: Color): void => {
    onOpen();
    setValues({
      id: value.id,
      colorCode: value.colorCode,
      labelEn: value.labelEn || '',
      labelAr: value.labelAr || '',
      // colorType: value.colorType,
      position: value.position,
      activeState: value?.activeState,
    });
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setColorStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <>
      <Page title={'Color: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Color'}
            links={[{ name: 'Dashboard', href: '/' }]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={onOpen}
              >
                {'New Color'}
              </Button>
            }
          />

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Color Code'}</TableCell>
                      <TableCell align='center'>{'English'}</TableCell>
                      <TableCell align='center'>{'Arabic'}</TableCell>
                      {/* <TableCell align='center'>{'Color Type'}</TableCell> */}
                      <TableCell align='center'>{'Position'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((color) => (
                        <TableRow key={color.id}>
                          <TableCell align='center'>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {color?.colorCode}
                              <div
                                style={{
                                  width: 18,
                                  height: 18,
                                  backgroundColor: color?.colorCode,
                                  marginLeft: 8,
                                  borderRadius: '50%',
                                }}
                              />
                            </div>
                          </TableCell>
                          <TableCell align='center'>
                            {color?.labelEn || '-'}
                          </TableCell>
                          <TableCell align='center'>
                            {color?.labelAr || '-'}
                          </TableCell>
                          {/* <TableCell align='center'>
                            {color?.colorType}
                          </TableCell> */}
                          <TableCell align='center'>
                            {color?.position}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={color.activeState ? 'success' : 'error'}
                            >
                              {color.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(color)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <ColorFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;
